import React, { useCallback, useMemo } from 'react'

import {
  LaunchIcon,
  LocationIcon,
  WarningOutlineIcon
} from '@toasttab/buffet-pui-icons'
import convert from 'convert-units'
import { orderBy } from 'lodash'
import { computeDistanceBetween } from 'spherical-geometry-js'

import { DiningOptionBehavior } from 'src/apollo/onlineOrdering'
import { ButtonType } from 'src/apollo/sites'
import { formatDeliveryAddress } from 'src/public/components/online_ordering/address_configs'
import {
  useFulfillment,
  useOOLocations
} from 'src/public/components/online_ordering/FulfillmentContext'

import { BrandedButton } from 'src/shared/components/common/button'
import { t } from 'src/translations'

import { formatAddress } from 'shared/components/common/form_input/LocationInput'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'

import { useCart } from 'public/components/online_ordering/CartContext'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { DistanceUnits } from 'public/components/pages/location_selection_page/LocationOption'

import { ScreenProps } from './DeliveryModal'
import { useDelivery } from 'src/public/components/online_ordering/DeliveryContext'
import { trackDiningOptionChangedV2 } from '../dining_behavior_toggle/fulfillmentTrackingUtils'
import useTracker from 'src/lib/js/hooks/useTracker'

const AddressInvalidScreen = ({
  goToScreen,
  closeModal,
  deliveryAddress,
  trackingSource
}: ScreenProps) => {
  const {
    canOrderTakeout,
    setSelectedDiningOptionBehavior,
    updateCartFulfillment,
    fulfillmentData
  } = useFulfillment()
  const { customerAddresses } = useDelivery()
  const { selectedLocation, updateLocation, ooRestaurant } = useRestaurant()
  const ooLocations = useOOLocations()
  const locationAlternatives = useMemo(
    () =>
      ooLocations.filter(
        (loc) => loc.externalId !== selectedLocation.externalId
      ),
    [ooLocations, selectedLocation]
  )
  const { cart } = useCart()
  const tracker = useTracker()
  const handleSwitchToPickup = useCallback(async () => {
    if (!canOrderTakeout) return
    setSelectedDiningOptionBehavior(DiningOptionBehavior.TakeOut)
    if (fulfillmentData?.cartFulfillmentData) {
      await updateCartFulfillment({
        fulfillmentType: fulfillmentData.cartFulfillmentData.fulfillmentType,
        fulfillmentDateTime:
          fulfillmentData.cartFulfillmentData.fulfillmentDateTime,
        diningOptionBehavior: DiningOptionBehavior.TakeOut,
        deliveryInfo: null
      })
      trackDiningOptionChangedV2(
        tracker,
        fulfillmentData.cartFulfillmentData.fulfillmentType,
        false,
        trackingSource,
        DiningOptionBehavior.TakeOut,
        null
      )
    }
    closeModal()
  }, [
    canOrderTakeout,
    setSelectedDiningOptionBehavior,
    fulfillmentData?.cartFulfillmentData,
    closeModal,
    updateCartFulfillment,
    tracker,
    trackingSource
  ])
  const { intlAddressCountryLogicErrorHandling } = useFlags()

  const handleTryAgain = useCallback(() => {
    if (customerAddresses.length > 0) {
      goToScreen('address-hub', null)
    } else {
      goToScreen('address-search')
    }
  }, [goToScreen, customerAddresses])

  const locationsWithDistance = useMemo(
    () =>
      orderBy(
        locationAlternatives?.map((loc) => {
          const distanceMeters = deliveryAddress
            ? computeDistanceBetween(
                { lat: loc.lat, long: loc.long },
                {
                  lat: deliveryAddress.latitude,
                  long: deliveryAddress.longitude
                }
              )
            : null

          // Use miles in the US and as the default, use kilometers everywhere else.
          // We could determine the units based on Restaurant config, but only checking if the Rx is in the US or UK seems good enough based on the data listed here:
          // https://github.toasttab.com/toasttab/library-localization-reference/blob/main/library-localization-reference/src/main/kotlin/com/toasttab/common/localization/reference/data/Countries.kt
          // Only 4 countries are listed as using miles as a distanceMeasurement: US, UK, Liberia, and Myanmar.
          const distanceUnits: DistanceUnits =
            loc.country && loc.country !== 'US' && loc.country !== 'GB'
              ? 'km'
              : 'mi'
          return {
            ...loc,
            distanceFromCustomer: distanceMeters
              ? convert(distanceMeters).from('m').to(distanceUnits)
              : -1,
            distanceUnits
          }
        }),
        'distanceFromCustomer',
        'asc'
      ).filter((loc) => loc.distanceFromCustomer <= 25),
    [deliveryAddress, locationAlternatives]
  )

  return (
    <div className='addressInvalidScreen' data-testid='address-invalid-screen'>
      {locationsWithDistance.length === 0 ? (
        <div className='modalContent'>
          <div className='addressInvalidContainer'>
            <LocationIcon size='xs' accessibility='decorative' />
            <h2 id='delivery-address-modal-title' tabIndex={-1}>
              {t('address-invalid-screen.cannot-deliver-to')}
            </h2>
            <p>
              {formatDeliveryAddress(
                deliveryAddress,
                {
                  full: true,
                  country: ooRestaurant?.i18n.country
                },
                intlAddressCountryLogicErrorHandling
              )}
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className='modalHeader'>
            <h2 id='delivery-address-modal-title' tabIndex={-1}>
              {t('address-invalid-screen.location-no-delivery')}
            </h2>
            <h3>{t('address-invalid-screen.another-location-might')}</h3>
          </div>
          {!!cart && (
            <div className='warningBanner'>
              <WarningOutlineIcon size='xs' accessibility='decorative' />
              <span>{t('address-invalid-screen.carts-dont-carry-over')}</span>
            </div>
          )}
          <div className='modalContent'>
            <div
              className='locationAlternatives'
              data-testid='location-alternatives'
            >
              {locationsWithDistance.map((loc) => (
                <button
                  className='locationOption'
                  key={loc.externalId}
                  onClick={() => updateLocation(loc.externalId)}
                  data-testid={`location-option-${loc.name}`}
                >
                  <div className='locationInfo'>
                    <div className='nameAndDistance'>
                      <div className='name'>{loc.name}</div>
                      <div className='distance'>{`(${loc.distanceFromCustomer.toFixed(
                        2
                      )} ${loc.distanceUnits})`}</div>
                    </div>
                    <div className='address'>{formatAddress(loc)}</div>
                  </div>
                  <LaunchIcon />
                </button>
              ))}
            </div>
          </div>
        </>
      )}
      <div className='footer stacked'>
        {canOrderTakeout ? (
          <BrandedButton
            onClick={handleSwitchToPickup}
            className='cancelButton'
          >
            {t('fulfillment-general.switch-to-pickup')}
          </BrandedButton>
        ) : (
          <BrandedButton onClick={closeModal} className='cancelButton'>
            {t('fulfillment-general.cancel')}
          </BrandedButton>
        )}
        <BrandedButton
          variant={ButtonType.Primary}
          onClick={handleTryAgain}
          testId='address-invalid-try-again'
        >
          {t('fulfillment-general.change-delivery-address')}
        </BrandedButton>
      </div>
    </div>
  )
}

export default AddressInvalidScreen
